import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import type { User, UserPreferences, ProfileRow } from "../types";
import { profileToUser } from "../types";

export function useProfile(userId: string | undefined) {
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger le profil utilisateur
  const loadProfile = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (fetchError) throw fetchError;
      // preferences (dont companyName, companySiret, etc.) sont optionnels en base
      setProfile(profileToUser(data as ProfileRow));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur de chargement du profil");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Upload d'avatar vers Supabase Storage
  const uploadAvatar = useCallback(
    async (file: File): Promise<{ url: string | null; error: string | null }> => {
      if (!userId) return { url: null, error: "Utilisateur non connecté" };

      try {
        // Vérifier le type de fichier
        if (!file.type.startsWith("image/")) {
          return { url: null, error: "Le fichier doit être une image" };
        }

        // Vérifier la taille (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
          return { url: null, error: "L'image ne doit pas dépasser 2MB" };
        }

        // Générer un nom de fichier unique dans un dossier = userId (requis par la RLS Storage)
        const fileExt = file.name.split(".").pop();
        const fileName = `${userId}-${Date.now()}.${fileExt}`;
        const filePath = `${userId}/${fileName}`;

        // Supprimer l'ancien avatar s'il existe (path = "userId/fichier" ou ancien format "fichier")
        if (profile?.avatar_url) {
          const pathInBucket = profile.avatar_url.includes("avatars/")
            ? profile.avatar_url.split("avatars/")[1]
            : profile.avatar_url.split("/").pop();
          if (pathInBucket) {
            await supabase.storage.from("avatars").remove([pathInBucket]);
          }
        }

        // Upload du nouveau fichier
        const { error: uploadError } = await supabase.storage
          .from("avatars")
          .upload(filePath, file, {
            cacheControl: "3600",
            upsert: true,
          });

        if (uploadError) throw uploadError;

        // Obtenir l'URL publique
        const { data: urlData } = supabase.storage
          .from("avatars")
          .getPublicUrl(filePath);

        const publicUrl = urlData.publicUrl;

        // Mettre à jour le profil avec la nouvelle URL
        const { error: updateError } = await supabase
          .from("profiles")
          .update({ avatar_url: publicUrl })
          .eq("id", userId);

        if (updateError) throw updateError;

        // Recharger le profil
        await loadProfile();

        return { url: publicUrl, error: null };
      } catch (e) {
        return {
          url: null,
          error: e instanceof Error ? e.message : "Erreur lors de l'upload",
        };
      }
    },
    [userId, profile?.avatar_url, loadProfile]
  );

  // Supprimer l'avatar
  const removeAvatar = useCallback(async (): Promise<{ error: string | null }> => {
    if (!userId) return { error: "Utilisateur non connecté" };

    try {
      // Supprimer le fichier du storage (path = "userId/fichier" ou ancien format "fichier")
      if (profile?.avatar_url) {
        const pathInBucket = profile.avatar_url.includes("avatars/")
          ? profile.avatar_url.split("avatars/")[1]
          : profile.avatar_url.split("/").pop();
        if (pathInBucket) {
          await supabase.storage.from("avatars").remove([pathInBucket]);
        }
      }

      // Mettre à jour le profil
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("id", userId);

      if (updateError) throw updateError;

      // Recharger le profil
      await loadProfile();

      return { error: null };
    } catch (e) {
      return {
        error: e instanceof Error ? e.message : "Erreur lors de la suppression",
      };
    }
  }, [userId, profile?.avatar_url, loadProfile]);

  // Mettre à jour les préférences (merge avec l'existant pour éviter d'écraser companyName, invoiceTemplate, etc.)
  const updatePreferences = useCallback(
    async (preferences: UserPreferences): Promise<{ error: string | null }> => {
      if (!userId) return { error: "Utilisateur non connecté" };

      try {
        const merged = { ...(profile?.preferences ?? {}), ...preferences };
        const { error: updateError } = await supabase
          .from("profiles")
          .update({ preferences: merged })
          .eq("id", userId);

        if (updateError) throw updateError;

        setProfile((prev) => (prev ? { ...prev, preferences: merged } : null));
        return { error: null };
      } catch (e) {
        return {
          error: e instanceof Error ? e.message : "Erreur lors de la mise à jour",
        };
      }
    },
    [userId, profile?.preferences]
  );

  // Changer le mot de passe
  const changePassword = useCallback(
    async (newPassword: string): Promise<{ error: string | null }> => {
      try {
        const { error: updateError } = await supabase.auth.updateUser({
          password: newPassword,
        });

        if (updateError) throw updateError;

        return { error: null };
      } catch (e) {
        return {
          error: e instanceof Error ? e.message : "Erreur lors du changement de mot de passe",
        };
      }
    },
    []
  );

  return {
    profile,
    loading,
    error,
    uploadAvatar,
    removeAvatar,
    updatePreferences,
    changePassword,
    reload: loadProfile,
  };
}
